import { Box, styled } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { Section, SectionTitle } from './styles';

import type { SourceMediaTypes } from 'features/source-media-tracks/enums';
import type { SourceMediaTrackDiagnostics } from 'features/source-media-tracks/services';
import { SourceMediaTracks } from 'features/source-media-tracks/services';
import theme from 'features/theme/theme';

type MediaTracksSnapshot = Partial<
  Record<SourceMediaTypes, SourceMediaTrackDiagnostics>
>;

export const MediaTracksTab = () => {
  const [expandedTrack, setExpandedTrack] = useState<string | null>(null);
  const [mediaTrackDiagnostics, setMediaTrackDiagnostics] =
    useState<MediaTracksSnapshot>(() => {
      try {
        return SourceMediaTracks.getInstance().getTrackDiagnostics();
      } catch (error) {
        console.warn(
          'Unable to initialize SourceMediaTracks diagnostics',
          error
        );
        return {};
      }
    });

  const mediaTrackEntries = useMemo(() => {
    return Object.entries(mediaTrackDiagnostics).map(
      ([sourceMediaType, diagnostics]) => ({
        sourceMediaType: sourceMediaType as SourceMediaTypes,
        diagnostics,
      })
    );
  }, [mediaTrackDiagnostics]);

  useEffect(function mediaTracksListener() {
    try {
      const sourceMediaTracks = SourceMediaTracks.getInstance();

      const handleTracksChanged = () => {
        setMediaTrackDiagnostics(sourceMediaTracks.getTrackDiagnostics());
      };

      handleTracksChanged();
      sourceMediaTracks.addStreamsChangedListener(handleTracksChanged);

      return () => {
        sourceMediaTracks.removeStreamsChangedListener(handleTracksChanged);
      };
    } catch (error) {
      console.warn(
        'Unable to subscribe to SourceMediaTracks diagnostics',
        error
      );
    }
  }, []);

  return (
    <Section>
      <SectionTitle variant='subtitle2'>Source Media Tracks</SectionTitle>
      {mediaTrackEntries.length === 0 && (
        <EmptyState>No active media tracks</EmptyState>
      )}
      {mediaTrackEntries.map(({ sourceMediaType, diagnostics }) => {
        if (!diagnostics) {
          return null;
        }

        const trackDetails = diagnostics.trackDetails;
        const isExpanded = expandedTrack === sourceMediaType;
        const availability = (() => {
          if (diagnostics.hasTrack) {
            return 'Attached';
          }
          if (diagnostics.isTrackUnavailable) {
            return 'Unavailable';
          }
          return 'Pending';
        })();

        const scope = diagnostics.isLocalType ? 'Local' : 'Remote';
        const state = trackDetails?.readyState ?? '—';
        const enabled = trackDetails?.enabled ? '✓' : '✗';

        return (
          <TrackRow key={sourceMediaType} $expanded={isExpanded}>
            <TrackSummary
              onClick={() =>
                setExpandedTrack(isExpanded ? null : sourceMediaType)
              }
            >
              <TrackIndicator $status={availability} />
              <TrackName>{sourceMediaType}</TrackName>
              <TrackBadge $variant='scope'>{scope}</TrackBadge>
              <TrackBadge $variant={availability.toLowerCase()}>
                {availability}
              </TrackBadge>
              <TrackBadge $variant='neutral'>{state}</TrackBadge>
              <TrackBadge $variant={enabled === '✓' ? 'good' : 'bad'}>
                {enabled}
              </TrackBadge>
              <ExpandArrow $expanded={isExpanded}>›</ExpandArrow>
            </TrackSummary>

            {isExpanded && (
              <TrackDetails>
                <DetailRow>
                  <DetailLabel>Device ID</DetailLabel>
                  <DetailValue>{diagnostics.deviceId ?? 'None'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Monitored ID</DetailLabel>
                  <DetailValue>
                    {diagnostics.monitoredTrackId ?? 'None'}
                  </DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Track Label</DetailLabel>
                  <DetailValue>{trackDetails?.label ?? 'N/A'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Track ID</DetailLabel>
                  <DetailValue>{trackDetails?.id ?? 'N/A'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Kind</DetailLabel>
                  <DetailValue>{trackDetails?.kind ?? 'N/A'}</DetailValue>
                </DetailRow>
                <DetailRow>
                  <DetailLabel>Muted</DetailLabel>
                  <DetailValue $warn={trackDetails?.muted ?? false}>
                    {(trackDetails?.muted ?? false) ? 'Yes' : 'No'}
                  </DetailValue>
                </DetailRow>
              </TrackDetails>
            )}
          </TrackRow>
        );
      })}
    </Section>
  );
};

const EmptyState = styled('div')({
  color: '#475569',
  fontSize: '0.85em',
  fontStyle: 'italic',
  textAlign: 'center',
  padding: theme.spacing(3, 0),
});

const TrackRow = styled(Box)<{ $expanded?: boolean }>(({ $expanded }) => ({
  marginBottom: theme.spacing(0.75),
  borderRadius: 8,
  overflow: 'hidden',
  border: $expanded
    ? '1px solid rgba(99, 102, 241, 0.2)'
    : '1px solid rgba(255, 255, 255, 0.04)',
  background: $expanded ? 'rgba(99, 102, 241, 0.03)' : 'transparent',
  transition: 'all 0.15s ease',
}));

const TrackSummary = styled('button')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
  padding: theme.spacing(1, 1.25),
  background: 'rgba(255, 255, 255, 0.02)',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  color: '#e5e7eb',
  fontSize: '0.82em',
  fontFamily:
    "'JetBrains Mono', 'Fira Code', 'SF Mono', 'Cascadia Code', monospace",
  textAlign: 'left',
  transition: 'background 0.15s',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.05)',
  },
}));

const TrackIndicator = styled('span')<{ $status: string }>(({ $status }) => ({
  width: 7,
  height: 7,
  borderRadius: '50%',
  flexShrink: 0,
  ...trackIndicatorColors[$status as TrackIndicatorStatus],
}));

type TrackIndicatorStatus = 'Attached' | 'Unavailable' | 'Pending';

const trackIndicatorColors: Record<
  TrackIndicatorStatus,
  { boxShadow: string; backgroundColor: string }
> = {
  Attached: {
    boxShadow: '0 0 6px rgba(74, 222, 128, 0.5)',
    backgroundColor: '#4ade80',
  },
  Unavailable: {
    boxShadow: '0 0 6px rgba(248, 113, 113, 0.5)',
    backgroundColor: '#f87171',
  },
  Pending: {
    boxShadow: '0 0 6px rgba(251, 191, 36, 0.4)',
    backgroundColor: '#fbbf24',
  },
};

const TrackName = styled('span')({
  color: '#e2e8f0',
  fontWeight: 600,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: '0.9em',
});

type BadgeVariant =
  | 'scope'
  | 'attached'
  | 'unavailable'
  | 'pending'
  | 'neutral'
  | 'good'
  | 'bad';

const badgeColors: Record<BadgeVariant, { bg: string; color: string }> = {
  scope: { bg: 'rgba(99, 102, 241, 0.12)', color: '#a5b4fc' },
  attached: { bg: 'rgba(74, 222, 128, 0.1)', color: '#6ee7b7' },
  unavailable: { bg: 'rgba(248, 113, 113, 0.1)', color: '#fca5a5' },
  pending: { bg: 'rgba(251, 191, 36, 0.1)', color: '#fcd34d' },
  neutral: { bg: 'rgba(255, 255, 255, 0.06)', color: '#94a3b8' },
  good: { bg: 'rgba(74, 222, 128, 0.1)', color: '#6ee7b7' },
  bad: { bg: 'rgba(248, 113, 113, 0.1)', color: '#fca5a5' },
};

const TrackBadge = styled('span')<{ $variant?: string }>(({ $variant }) => {
  const variant = ($variant ?? 'neutral') as BadgeVariant;
  const colors = badgeColors[variant] ?? badgeColors.neutral;
  return {
    padding: '2px 6px',
    borderRadius: 4,
    background: colors.bg,
    color: colors.color,
    fontSize: '0.72em',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    letterSpacing: '0.02em',
  };
});

const ExpandArrow = styled('span')<{ $expanded?: boolean }>(
  ({ $expanded }) => ({
    color: '#64748b',
    fontSize: '0.9em',
    flexShrink: 0,
    transition: 'transform 0.15s ease',
    transform: $expanded ? 'rotate(90deg)' : 'rotate(0deg)',
  })
);

const TrackDetails = styled(Box)(() => ({
  padding: theme.spacing(1.25, 1.5, 1.25, 2.5),
  borderTop: '1px solid rgba(99, 102, 241, 0.08)',
  background: 'rgba(0, 0, 0, 0.15)',
}));

const DetailRow = styled('div')(() => ({
  display: 'grid',
  gridTemplateColumns: '100px 1fr',
  gap: theme.spacing(1),
  padding: theme.spacing(0.3, 0),
  fontSize: '0.82em',
}));

const DetailLabel = styled('span')({
  color: '#64748b',
  fontWeight: 500,
});

const DetailValue = styled('span')<{ $warn?: boolean }>(({ $warn }) => ({
  color: $warn ? '#fca5a5' : '#6ee7b7',
  fontFamily:
    "'JetBrains Mono', 'Fira Code', 'SF Mono', 'Cascadia Code', monospace",
  fontWeight: 500,
  wordBreak: 'break-all',
}));
